import { router, usePage } from '@inertiajs/react'
import { ChevronDown, ChevronRight, Download, SlidersHorizontal, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { reportsUnitsPath, reportsUnitsCsvPath, reportsUnitsPdfPath } from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebReportUnitRow from '@/shared/types/reports/WebReportUnitRow'
import { ReportEmptyState } from '../components/ReportEmptyState'
import { ReportMobileField } from '../components/ReportMobileField'
import { ReportRangeOrderError } from '../components/ReportRangeOrderError'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import QaAlertBadge from '../components/QaAlertBadge'
import { QA_ALERT_CATEGORY_MAP } from '@/shared/constants/qaAlertConfig'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { downloadExport } from '@/shared/lib/downloadExport'

interface FiltersShape {
  fromDate?: string
  toDate?: string
  hideEmpty?: boolean
}

interface Props {
  rows: WebReportUnitRow[]
  meta: PaginationMeta
  filters: FiltersShape
  defaultFromDate: string
  defaultToDate: string
  [key: string]: unknown
}

const UnitsReportPage = () => {
  const { rows, meta, filters, defaultFromDate, defaultToDate, complianceSettings } = usePage<
    Props & SharedProps
  >().props

  useBreadcrumbs([{ label: t('nav.unitsReport') }])

  const [fromDate, setFromDate] = useState(filters.fromDate ?? defaultFromDate)
  const [toDate, setToDate] = useState(filters.toDate ?? defaultToDate)
  const [hideEmpty, setHideEmpty] = useState(filters.hideEmpty ?? false)
  const [expandedUnitIds, setExpandedUnitIds] = useState<Set<number>>(new Set())
  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()

  // Syncs local state from server props after each Inertia navigation.
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- mirror URL-driven Inertia props (camelCase) */
    setFromDate(filters.fromDate ?? defaultFromDate)
    setToDate(filters.toDate ?? defaultToDate)
    setHideEmpty(filters.hideEmpty ?? false)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [filters.fromDate, filters.toDate, filters.hideEmpty, defaultFromDate, defaultToDate])

  const dateRangeInvalid = isReportRangeOrderInvalid(fromDate, toDate)

  const buildFilterParams = (
    overrides: {
      fromDate?: string
      toDate?: string
      hideEmpty?: boolean
    } = {},
  ) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    const he = overrides.hideEmpty ?? hideEmpty
    const params: Record<string, string> = {}
    if (fd) params.from_date = fd
    if (td) params.to_date = td
    if (he) params.hide_empty = '1'
    return params
  }

  const navigate = (
    overrides: {
      fromDate?: string
      toDate?: string
      hideEmpty?: boolean
    } = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    if (isReportRangeOrderInvalid(fd, td)) return
    const params = buildFilterParams(overrides)
    const visit = beginVisit(
      JSON.stringify(params),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    router.get(reportsUnitsPath(), params, {
      preserveState: visit.keepDropdownOpen,
      preserveScroll: visit.keepDropdownOpen,
    })
  }

  const handleFromDateChange = (val: string) => {
    setFromDate(val)
    navigate({ fromDate: val })
  }

  const handleToDateChange = (val: string) => {
    setToDate(val)
    navigate({ toDate: val })
  }

  const handleHideEmptyChange = (checked: boolean) => {
    setHideEmpty(checked)
    navigate({ hideEmpty: checked })
  }

  const clearFilters = () => {
    setFromDate(defaultFromDate)
    setToDate(defaultToDate)
    setHideEmpty(false)
    router.get(reportsUnitsPath(), {}, { preserveState: false })
  }

  const toggleUnit = (unitId: number) => {
    setExpandedUnitIds((prev) => {
      const next = new Set(prev)
      if (next.has(unitId)) {
        next.delete(unitId)
      } else {
        next.add(unitId)
      }
      return next
    })
  }

  const handlePageChange = (page: number) => {
    if (dateRangeInvalid) return
    router.get(
      reportsUnitsPath(),
      { ...buildFilterParams(), page: String(page) },
      { preserveState: false },
    )
  }

  const handleExportCsv = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsUnitsCsvPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const handleExportPdf = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsUnitsPdfPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  const activeFilterCount = [
    fromDate !== defaultFromDate,
    toDate !== defaultToDate,
    hideEmpty,
  ].filter(Boolean).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-start">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.units.fromDate')}</Label>
        <DatePickerInput
          value={fromDate}
          onChange={handleFromDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.units.toDate')}</Label>
        <DatePickerInput
          value={toDate}
          onChange={handleToDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>
      <div className="flex items-center gap-2 self-end pb-1">
        <Checkbox
          id="hide-empty"
          checked={hideEmpty}
          onCheckedChange={(val) => handleHideEmptyChange(val === true)}
        />
        <Label htmlFor="hide-empty" className="cursor-pointer text-sm">
          {t('reports.units.hideEmpty')}
        </Label>
      </div>
      <ReportRangeOrderError show={dateRangeInvalid} />
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="min-w-0 text-xl font-bold sm:text-2xl">{t('reports.units.title')}</h1>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.units.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPdf}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.units.exportPdf')}
          </Button>

          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                if (!open) {
                  const hasChanges =
                    fromDate !== (filters.fromDate ?? defaultFromDate) ||
                    toDate !== (filters.toDate ?? defaultToDate) ||
                    hideEmpty !== (filters.hideEmpty ?? false)
                  if (hasChanges) navigate({}, 'sheet-close')
                }
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('reports.units.filters')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('reports.units.filters')}
                hideCloseButton
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-semibold">{t('reports.units.filters')}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('reports.units.clearFilters')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon" aria-label={t('reports.units.filters')}>
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {filterFields}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('reports.units.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <ReportEmptyState message={t('reports.units.noRecords')} />
      ) : (
        <div className="space-y-4">
          {/* Mobile cards */}
          <div className="space-y-2 lg:hidden">
            {rows.map((row) => {
              const expanded = expandedUnitIds.has(row.id)
              return (
                <div key={row.id} className="rounded-lg border bg-card shadow-sm">
                  <button
                    type="button"
                    className="flex w-full items-start justify-between p-3 text-left"
                    onClick={() => toggleUnit(row.id)}
                    aria-label={t('reports.units.expandUnit')}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground [overflow-wrap:anywhere]">
                        {row.name}
                      </p>
                      <p className="mt-0.5 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                        {[row.departmentName, row.siteName].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    {expanded ? (
                      <ChevronDown className="ml-2 mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="ml-2 mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </button>
                  <div className="px-3 pb-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <ReportMobileField label={t('reports.units.colInstruments')}>
                        {row.instrumentCount}
                      </ReportMobileField>
                      <ReportMobileField label={t('reports.units.colAvgCompliance')}>
                        <ComplianceBadge
                          percent={row.avgCompliancePercent}
                          colorCodes={complianceSettings?.colorCodes}
                        />
                      </ReportMobileField>
                    </div>
                    {expanded && (
                      <div className="mt-3 rounded-md border-t-2 border-primary/10 bg-muted/50 px-3 pt-3 pb-2">
                        <div className="mb-2 flex flex-wrap gap-2">
                          <QaAlertBadge
                            category="failures"
                            label={t(QA_ALERT_CATEGORY_MAP.failures.labelKey)}
                            count={row.alertCounts.failureCount}
                          />
                          <QaAlertBadge
                            category="nonCompliant"
                            label={t(QA_ALERT_CATEGORY_MAP.nonCompliant.labelKey)}
                            count={row.alertCounts.nonComplianceCount}
                          />
                          <QaAlertBadge
                            category="serviceReports"
                            label={t(QA_ALERT_CATEGORY_MAP.serviceReports.labelKey)}
                            count={row.alertCounts.serviceReportCount}
                          />
                          <QaAlertBadge
                            category="dateChanges"
                            label={t(QA_ALERT_CATEGORY_MAP.dateChanges.labelKey)}
                            count={row.alertCounts.dateChangeCount}
                          />
                        </div>
                        {row.instruments.length > 0 && (
                          <div className="space-y-1">
                            {row.instruments.map((ins) => (
                              <div
                                key={ins.id}
                                className="flex items-center justify-between gap-2 rounded bg-muted/40 px-2 py-1 text-xs"
                              >
                                <span className="font-medium [overflow-wrap:anywhere]">
                                  {ins.instrumentLabel}
                                  {ins.isRetired && (
                                    <Badge variant="secondary" className="ml-1 text-xs">
                                      {t('reports.units.colRetired')}
                                    </Badge>
                                  )}
                                </span>
                                <span className="shrink-0">
                                  <ComplianceBadge
                                    percent={ins.avgCompliancePercent}
                                    colorCodes={complianceSettings?.colorCodes}
                                  />
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>{t('reports.units.colUnit')}</TableHead>
                    <TableHead>{t('reports.units.colDepartment')}</TableHead>
                    <TableHead>{t('reports.units.colSite')}</TableHead>
                    <TableHead className="text-right">
                      {t('reports.units.colInstruments')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('reports.units.colAvgCompliance')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const expanded = expandedUnitIds.has(row.id)
                    return (
                      <Collapsible
                        key={row.id}
                        open={expanded}
                        onOpenChange={() => toggleUnit(row.id)}
                        asChild
                      >
                        <>
                          <TableRow className="cursor-pointer hover:bg-muted/50">
                            <TableCell className="py-2">
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                  {expanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            </TableCell>
                            <TableCell className="font-medium" onClick={() => toggleUnit(row.id)}>
                              {row.name}
                            </TableCell>
                            <TableCell
                              className="text-muted-foreground"
                              onClick={() => toggleUnit(row.id)}
                            >
                              {row.departmentName}
                            </TableCell>
                            <TableCell
                              className="text-muted-foreground"
                              onClick={() => toggleUnit(row.id)}
                            >
                              {row.siteName}
                            </TableCell>
                            <TableCell className="text-right" onClick={() => toggleUnit(row.id)}>
                              {row.instrumentCount}
                            </TableCell>
                            <TableCell className="text-right" onClick={() => toggleUnit(row.id)}>
                              <ComplianceBadge
                                percent={row.avgCompliancePercent}
                                colorCodes={complianceSettings?.colorCodes}
                              />
                            </TableCell>
                          </TableRow>
                          <CollapsibleContent asChild>
                            <tr>
                              <td
                                colSpan={6}
                                className="border-t-2 border-primary/10 bg-muted/50 px-6 py-4"
                              >
                                <div className="rounded-md bg-background/60 p-3">
                                  <div className="mb-3 flex flex-wrap gap-2">
                                    <QaAlertBadge
                                      category="failures"
                                      label={t(QA_ALERT_CATEGORY_MAP.failures.labelKey)}
                                      count={row.alertCounts.failureCount}
                                    />
                                    <QaAlertBadge
                                      category="nonCompliant"
                                      label={t(QA_ALERT_CATEGORY_MAP.nonCompliant.labelKey)}
                                      count={row.alertCounts.nonComplianceCount}
                                    />
                                    <QaAlertBadge
                                      category="serviceReports"
                                      label={t(QA_ALERT_CATEGORY_MAP.serviceReports.labelKey)}
                                      count={row.alertCounts.serviceReportCount}
                                    />
                                    <QaAlertBadge
                                      category="dateChanges"
                                      label={t(QA_ALERT_CATEGORY_MAP.dateChanges.labelKey)}
                                      count={row.alertCounts.dateChangeCount}
                                    />
                                  </div>
                                  {row.instruments.length > 0 && (
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-muted-foreground">
                                          <th className="pb-1 text-left font-medium">
                                            {t('reports.units.colInstrument')}
                                          </th>
                                          <th className="pb-1 text-left font-medium">
                                            {t('reports.units.colSerialNumber')}
                                          </th>
                                          <th className="pb-1 text-right font-medium">
                                            {t('reports.units.colCompliance')}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.instruments.map((ins) => (
                                          <tr key={ins.id} className="border-t border-border/40">
                                            <td className="py-1 [overflow-wrap:anywhere]">
                                              {ins.instrumentLabel}
                                              {ins.isRetired && (
                                                <Badge variant="secondary" className="ml-1 text-xs">
                                                  {t('reports.units.colRetired')}
                                                </Badge>
                                              )}
                                            </td>
                                            <td className="py-1 text-muted-foreground">
                                              {ins.serialNumber || '—'}
                                            </td>
                                            <td className="py-1 text-right">
                                              <ComplianceBadge
                                                percent={ins.avgCompliancePercent}
                                                colorCodes={complianceSettings?.colorCodes}
                                              />
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <DataPagination
            meta={meta}
            onPageChange={handlePageChange}
            noun={t('reports.units.paginationNoun')}
          />
        </div>
      )}
    </div>
  )
}

export default UnitsReportPage
